import { ProjectData, BoundaryPoint } from "../components/types";

/**
 * Transform backend project data to frontend format
 * Converts snake_case to camelCase and restructures data
 */
export const transformBackendToFrontend = (
  backendProject: any
): ProjectData => {
  return {
    id: backendProject.project_id,
    name: backendProject.name || "",
    description: backendProject.description || "",
    company_id: backendProject.company_id || "",
    location: backendProject.location || null,
    objects: backendProject.objects || [],
    boundaryPoints: transformBoundaryPointsBackendToFrontend(
      backendProject.boundary_points || []
    ),
    lastModified:
      backendProject.lastModified ||
      backendProject.updated_at ||
      backendProject.created_at ||
      "",
    status: backendProject.status || "active",
    activePanel: backendProject.active_panel || "boundary",
  };
};

/**
 * Transform frontend project data to backend format
 * Converts camelCase to snake_case and restructures data
 */
export const transformFrontendToBackend = (
  frontendProject: ProjectData
): any => {
  // Extract company_id from the project or from location.company if present
  let company_id = frontendProject.company_id;
  let location = frontendProject.location;

  // Handle case where company data is embedded in location (from LocationSelector)
  if (location && location.company) {
    company_id = location.company.company_id;
    // Remove company from location to avoid sending nested company data
    const { company, ...locationWithoutCompany } = location;
    location = locationWithoutCompany;
  }

  return {
    name: frontendProject.name,
    description: frontendProject.description,
    company_id: company_id,
    status: frontendProject.status,
    location: location,
    objects: frontendProject.objects,
    boundary_points: transformBoundaryPointsFrontendToBackend(
      frontendProject.boundaryPoints || []
    ),
    active_panel: frontendProject.activePanel,
  };
};

/**
 * Transform backend boundary points to frontend format
 * Converts flat coordinates to nested structure
 */
export const transformBoundaryPointsBackendToFrontend = (
  backendPoints: any[]
): BoundaryPoint[] => {
  if (!Array.isArray(backendPoints)) return [];

  return backendPoints.map((point) => ({
    id: point.id || `temp-${Date.now()}-${point.order}`,
    coordinates: {
      latitude: point.latitude || 0,
      longitude: point.longitude || 0,
    },
    order: point.order || 0,
  }));
};

/**
 * Transform frontend boundary points to backend format
 * Converts nested coordinates to flat structure
 */
export const transformBoundaryPointsFrontendToBackend = (
  frontendPoints: BoundaryPoint[]
): any[] => {
  if (!Array.isArray(frontendPoints)) return [];

  return frontendPoints.map((point) => ({
    id: point.id,
    latitude: point.coordinates.latitude,
    longitude: point.coordinates.longitude,
    order: point.order,
  }));
};

/**
 * Transform a list of backend projects to frontend format
 */
export const transformProjectsListBackendToFrontend = (
  backendProjects: any[]
): ProjectData[] => {
  if (!Array.isArray(backendProjects)) return [];

  return backendProjects.map((project) => transformBackendToFrontend(project));
};
